import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Logo from '../components/Logo';
import UpgradeScreen from '../components/UpgradeScreen';
import { hasFullAccess } from '../lib/platform';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { 
  Search, 
  Bot, 
  Home, 
  MapPin, 
  Euro, 
  Ruler, 
  TrendingUp, 
  AlertTriangle,
  Heart,
  ExternalLink,
  Loader2,
  Building2,
  ChevronLeft,
  History,
  Sparkles,
  Target,
  BarChart3,
  CheckCircle2,
  XCircle,
  Clock,
  LogOut,
  User,
  Menu,
  X,
  Eye,
  ArrowLeft,
  RefreshCw,
  MessageCircle,
  Send,
  Upload,
  FileText,
  Trash2,
  Crown,
  Calculator,
  Banknote,
  CloudSun,
  Tag,
  Zap,
  ChevronDown,
  ChevronUp,
  Briefcase,
  Save as SaveIcon,
  Mic,
  MicOff
} from 'lucide-react';
import { isNativePlatform } from '../lib/platform';
import AIConsentModal from '../components/AIConsentModal';
import { hasAIConsent } from '../lib/aiConsent';

const AIAgentPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [results, setResults] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [savedListings, setSavedListings] = useState([]);
  const [viewingHistoricSearch, setViewingHistoricSearch] = useState(null);
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // NEW: Chat and tabs state
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'search'
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [showSkills, setShowSkills] = useState(false);
  const [expandedSkillCards, setExpandedSkillCards] = useState({});
  const [savedAnalyses, setSavedAnalyses] = useState({});
  const fileInputRef = React.useRef(null);
  const chatEndRef = React.useRef(null);
  const recognitionRef = React.useRef(null);
  const [isListening, setIsListening] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'fr-FR';

      let finalTranscript = '';

      recognitionRef.current.onresult = (event) => {
        let interim = '';
        finalTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interim += event.results[i][0].transcript;
          }
        }
        setChatInput(finalTranscript || interim);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        if (finalTranscript.trim()) {
          setTimeout(() => {
            const sendBtn = document.querySelector('[data-testid="ai-agent-send-btn"]');
            if (sendBtn && !sendBtn.disabled) sendBtn.click();
          }, 300);
        }
      };

      recognitionRef.current.onerror = (event) => {
        setIsListening(false);
        if (event.error === 'not-allowed') toast.error('Autorise le micro dans ton navigateur');
        else if (event.error === 'network') toast.error('Erreur reseau - verifie ta connexion');
        else if (event.error === 'no-speech') toast.info('Aucune voix detectee, reessaie');
      };
    }
  }, []);

  const toggleListening = async () => {
    if (!recognitionRef.current) {
      toast.error('La reconnaissance vocale n\'est pas supportée par ton navigateur');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      // Request microphone permission explicitly before starting
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        toast.error('Veuillez autoriser l\'accès au microphone dans les réglages de votre appareil');
        return;
      }
      setChatInput('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  // Search criteria state - check URL params for MDB prefill
  const [criteria, setCriteria] = useState({
    localisation: searchParams.get('localisation') || 'Lyon',
    type_bien: searchParams.get('type_bien') || 'appartement',
    budget_min: searchParams.get('budget_min') ? parseFloat(searchParams.get('budget_min')) : null,
    budget_max: searchParams.get('budget_max') ? parseFloat(searchParams.get('budget_max')) : 300000,
    surface_min: searchParams.get('surface_min') ? parseFloat(searchParams.get('surface_min')) : null,
    surface_max: searchParams.get('surface_max') ? parseFloat(searchParams.get('surface_max')) : null,
    nb_pieces_min: null,
    nb_pieces_max: null,
    rendement_min: searchParams.get('rendement_min') ? parseFloat(searchParams.get('rendement_min')) : 5,
    cash_flow_min: null
  });

  // Check if coming from MDB
  const fromMDB = searchParams.get('from') === 'mdb';
  const mdbProjectName = searchParams.get('project_name');

  // Check subscription tier
  const isClub = hasFullAccess(user?.subscription_tier);
  const isExpert = isClub;
  const isFree = !isClub;

  // Fetch usage stats and available skills on mount
  useEffect(() => {
    fetchUsageStats();
    fetchSkills();
  }, []);

  const fetchUsageStats = async () => {
    try {
      const response = await axios.get(`${API}/ai-chat/usage`, { withCredentials: true });
      setUsageStats(response.data);
    } catch (error) {
      console.error('Failed to fetch usage stats:', error);
    }
  };

  const fetchSkills = async () => {
    try {
      const response = await axios.get(`${API}/ai-skills/available`);
      setAvailableSkills(response.data.skills || []);
    } catch (error) {
      console.error('Failed to fetch skills:', error);
    }
  };

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Chat functions - using skills-enabled endpoint
  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;

    // Check rate limit for free users
    if (isFree && usageStats?.chat?.remaining <= 0) {
      toast.error('Tu as atteint ta limite de 3 questions par jour. Passe au Club !');
      return;
    }

    const userMessage = { role: 'user', content: chatInput, timestamp: new Date().toISOString() };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai-skills/chat`,
        { message: userMessage.content, conversation_id: conversationId },
        { withCredentials: true }
      );
      setConversationId(response.data.conversation_id);
      setChatMessages(prev => [...prev, response.data.message]);
      // Update usage stats
      if (response.data.remaining_questions !== null) {
        setUsageStats(prev => ({
          ...prev,
          chat: { ...prev?.chat, remaining: response.data.remaining_questions }
        }));
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = error.response?.data?.detail?.message || error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(errorMsg);
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setChatLoading(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isClub) {
      toast.error('L\'upload de PDF est réservé aux membres Club');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }

    setChatLoading(true);
    setChatMessages(prev => [...prev, { role: 'user', content: `📄 ${file.name}`, has_pdf: true, timestamp: new Date().toISOString() }]);

    const formData = new FormData();
    formData.append('file', file);
    if (conversationId) formData.append('conversation_id', conversationId);

    try {
      const response = await axios.post(
        `${API}/api/ai-chat/upload-pdf`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setConversationId(response.data.conversation_id);
      setChatMessages(prev => [...prev, response.data.message]);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setChatLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearChat = () => {
    setChatMessages([]);
    setConversationId(null);
  };

  useEffect(() => {
    if (isExpert) {
      fetchSearchHistory();
    }
  }, [isExpert]);

  // Auto-search if coming from MDB with criteria
  useEffect(() => {
    if (fromMDB && isExpert && criteria.localisation) {
      toast.info(`Recherche de biens pour le projet "${mdbProjectName || 'MDB'}"`);
    }
  }, [fromMDB, isExpert]);

  const fetchSearchHistory = async () => {
    try {
      const response = await axios.get(`${API}/ai-agent/searches`, { withCredentials: true });
      setSearchHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch search history:', error);
    }
  };

  const loadHistoricSearch = async (searchId) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/ai-agent/searches/${searchId}`, { withCredentials: true });
      setResults({
        criteria: response.data.criteria,
        total_results: response.data.results_count,
        listings: response.data.listings || [],
        search_id: response.data.search_id
      });
      setViewingHistoricSearch(response.data);
      setCriteria(response.data.criteria);
      setShowHistory(false);
      toast.success('Recherche historique chargée');
    } catch (error) {
      console.error('Failed to load historic search:', error);
      toast.error('Impossible de charger cette recherche');
    } finally {
      setLoadingHistory(false);
    }
  };

  const clearHistoricView = () => {
    setViewingHistoricSearch(null);
    setResults(null);
  };

  // Loading steps and messages for better UX
  const loadingSteps = [
    { label: 'Recherche', description: 'Scan des annonces...' },
    { label: 'Collecte', description: 'Récupération des données...' },
    { label: 'Analyse IA', description: 'Évaluation par Claude...' },
    { label: 'Finalisation', description: 'Préparation des résultats...' }
  ];

  const loadingTips = [
    "💡 Conseil : Un rendement brut supérieur à 7% est considéré comme excellent",
    "💡 Le cash-flow positif est la clé d'un investissement réussi",
    "💡 N'oubliez pas de vérifier les charges de copropriété",
    "💡 La localisation reste le critère n°1 en immobilier",
    "💡 Analysez toujours le marché locatif local avant d'acheter",
    "💡 Un bien à rénover peut cacher de bonnes opportunités",
    "💡 Pensez à inclure les frais de notaire dans votre calcul"
  ];

  // Simulate loading progress
  useEffect(() => {
    let stepInterval;
    let messageInterval;
    
    if (loading) {
      // Progress through steps
      stepInterval = setInterval(() => {
        setLoadingStep(prev => {
          if (prev < loadingSteps.length - 1) return prev + 1;
          return prev;
        });
      }, 15000); // Change step every 15 seconds

      // Rotate tips
      let tipIndex = 0;
      setLoadingMessage(loadingTips[0]);
      messageInterval = setInterval(() => {
        tipIndex = (tipIndex + 1) % loadingTips.length;
        setLoadingMessage(loadingTips[tipIndex]);
      }, 8000); // Change tip every 8 seconds
    } else {
      setLoadingStep(0);
      setLoadingMessage('');
    }

    return () => {
      clearInterval(stepInterval);
      clearInterval(messageInterval);
    };
  }, [loading]);

  // Poll for task status
  const pollTaskStatus = async (taskId) => {
    try {
      const response = await axios.get(`${API}/ai-agent/task/${taskId}`, { withCredentials: true });
      const { status, progress, step, result, error } = response.data;
      
      // Update loading state with real progress
      setLoadingStep(Math.floor(progress / 25)); // Convert 0-100 to 0-4 steps
      setLoadingMessage(step);
      
      if (status === 'completed' && result) {
        setResults(result);
        fetchSearchHistory();
        toast.success(`${result.total_results} annonces trouvées et analysées`);
        setLoading(false);
        return true; // Stop polling
      } else if (status === 'failed') {
        toast.error(error || 'Erreur lors de la recherche');
        setLoading(false);
        return true; // Stop polling
      }
      
      return false; // Continue polling
    } catch (error) {
      console.error('Poll error:', error);
      // Don't stop polling on network errors, retry
      return false;
    }
  };

  const handleSearch = async () => {
    if (!isExpert) {
      toast.error('Cette fonctionnalité est réservée aux abonnés Club');
      return;
    }

    setLoading(true);
    setResults(null);
    setLoadingStep(0);

    try {
      const searchData = {
        ...criteria,
        budget_max: criteria.budget_max || 500000,
        localisation: criteria.localisation || 'Lyon'
      };

      // Start async search
      const response = await axios.post(`${API}/ai-agent/search`, searchData, { withCredentials: true });
      const { task_id } = response.data;
      
      if (task_id) {
        // Poll for results
        const poll = async () => {
          const done = await pollTaskStatus(task_id);
          if (!done) {
            setTimeout(poll, 2000); // Poll every 2 seconds
          }
        };
        poll();
      } else {
        // Fallback: result returned directly (legacy)
        setResults(response.data);
        fetchSearchHistory();
        toast.success(`${response.data.total_results} annonces trouvées et analysées`);
        setLoading(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la recherche');
      setLoading(false);
    }
  };

  const handleSaveListing = async (listing) => {
    try {
      await axios.post(`${API}/ai-agent/save-listing`, listing, { withCredentials: true });
      setSavedListings([...savedListings, listing.listing_id]);
      toast.success('Annonce sauvegardée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const getRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case 'ACHETER':
        return 'bg-green-500 text-white';
      case 'NÉGOCIER':
        return 'bg-amber-500 text-white';
      case 'ÉVITER':
        return 'bg-red-500 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const toggleSkillCard = (idx) => {
    setExpandedSkillCards(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const saveToPortfolio = async (skillData) => {
    try {
      await axios.post(`${API}/portfolio/save`, {
        bien: skillData.bien,
        loyer_estime: skillData.loyer_estime,
        rentabilite: skillData.rentabilite,
        credit_25ans: skillData.credit_25ans,
        verdict: skillData.verdict,
      }, { withCredentials: true });
      setSavedAnalyses(prev => ({ ...prev, [JSON.stringify(skillData.bien)]: true }));
      toast.success('Analyse sauvegardée dans ton portfolio !');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Le portfolio est réservé aux membres Club');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    }
  };

  const analyzeProperty = async (bien, index) => {
    if (chatLoading) return;
    const msg = `Analyse la rentabilité du bien n°${index + 1} : "${bien.titre}" à ${bien.prix?.toLocaleString()}€, ${bien.surface || '?'}m², ${bien.localisation || ''}${bien.url ? ', url: ' + bien.url : ''}`;
    const userMessage = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    setChatMessages(prev => [...prev, userMessage]);
    setChatLoading(true);
    try {
      const response = await axios.post(
        `${API}/ai-skills/chat`,
        { message: msg, conversation_id: conversationId },
        { withCredentials: true }
      );
      setConversationId(response.data.conversation_id);
      setChatMessages(prev => [...prev, response.data.message]);
      if (response.data.remaining_questions !== null) {
        setUsageStats(prev => ({ ...prev, chat: { ...prev?.chat, remaining: response.data.remaining_questions } }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Erreur lors de l\'analyse');
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setChatLoading(false);
    }
  };

  const getSkillIcon = (skillId) => {
    const icons = {
      calculer_rentabilite: <Calculator className="h-4 w-4" />,
      simuler_credit: <Banknote className="h-4 w-4" />,
      rechercher_dvf: <Search className="h-4 w-4" />,
      diagnostic_climatique: <CloudSun className="h-4 w-4" />,
      estimer_prix: <Tag className="h-4 w-4" />,
      rechercher_biens: <Home className="h-4 w-4" />,
      analyser_bien: <TrendingUp className="h-4 w-4" />,
    };
    return icons[skillId] || <Zap className="h-4 w-4" />;
  };

  const renderSkillData = (msg, idx) => {
    if (!msg.skill_data || !msg.skill_used) return null;
    const data = msg.skill_data;
    const expanded = expandedSkillCards[idx];

    if (msg.skill_used === 'calculer_rentabilite') {
      return (
        <div data-testid="skill-card-rentabilite" className="mt-3 bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => toggleSkillCard(idx)}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#00B050] to-[#0D47A1] flex items-center justify-center">
                <Calculator className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-sm text-slate-700">Analyse de Rentabilité</span>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xs text-slate-500 uppercase">Rendement Brut</div>
              <div className="text-xl font-bold text-[#0D47A1]">{data.rendement_brut?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xs text-slate-500 uppercase">Rendement Net</div>
              <div className="text-xl font-bold text-[#00B050]">{data.rendement_net?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xs text-slate-500 uppercase">Cash-flow/mois</div>
              <div className={`text-xl font-bold ${data.cash_flow_mensuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.cash_flow_mensuel >= 0 ? '+' : ''}{data.cash_flow_mensuel?.toLocaleString()}€
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xs text-slate-500 uppercase">Mensualité</div>
              <div className="text-xl font-bold text-slate-700">{data.mensualite?.toLocaleString()}€</div>
            </div>
          </div>
          {expanded && (
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Coût total</span><span className="font-medium">{data.cout_total?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Emprunt</span><span className="font-medium">{data.montant_emprunt?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Loyers annuels</span><span className="font-medium">{data.loyers_annuels?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Impôts annuels</span><span className="font-medium text-red-600">-{data.impots_annuels?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Régime recommandé</span><Badge className="bg-[#0D47A1] text-white text-xs">{data.regime_label}</Badge></div>
            </div>
          )}
        </div>
      );
    }

    if (msg.skill_used === 'simuler_credit') {
      return (
        <div data-testid="skill-card-credit" className="mt-3 bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => toggleSkillCard(idx)}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
                <Banknote className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-sm text-slate-700">Simulation de Crédit</span>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xs text-slate-500 uppercase">Mensualité</div>
              <div className="text-xl font-bold text-[#0D47A1]">{data.mensualite?.toLocaleString()}€</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xs text-slate-500 uppercase">Coût total</div>
              <div className="text-xl font-bold text-slate-700">{data.cout_total_credit?.toLocaleString()}€</div>
            </div>
          </div>
          {expanded && data.tableau_amortissement && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-500 mb-2 uppercase">Tableau d'amortissement</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500">
                      <th className="text-left py-1">Année</th>
                      <th className="text-right py-1">Capital</th>
                      <th className="text-right py-1">Intérêts</th>
                      <th className="text-right py-1">Restant dû</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tableau_amortissement.map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-1">{row.annee}</td>
                        <td className="text-right text-green-600">{row.capital_rembourse?.toLocaleString()}€</td>
                        <td className="text-right text-red-500">{row.interets?.toLocaleString()}€</td>
                        <td className="text-right font-medium">{row.capital_restant?.toLocaleString()}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-slate-500">Coût des intérêts</span>
                <span className="font-medium text-red-600">{data.cout_interets?.toLocaleString()}€</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (msg.skill_used === 'rechercher_dvf') {
      const stats = data.stats || {};
      return (
        <div data-testid="skill-card-dvf" className="mt-3 bg-gradient-to-br from-slate-50 to-amber-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => toggleSkillCard(idx)}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                <Search className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-sm text-slate-700">DVF - {data.commune}</span>
              <Badge variant="outline" className="text-xs">{stats.nb_transactions || 0} transactions</Badge>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center border border-slate-100">
              <div className="text-[10px] sm:text-xs text-slate-500 uppercase">Prix moyen</div>
              <div className="text-sm sm:text-lg font-bold text-[#0D47A1]">{stats.prix_moyen?.toLocaleString()}€</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center border border-slate-100">
              <div className="text-[10px] sm:text-xs text-slate-500 uppercase">Prix/m²</div>
              <div className="text-sm sm:text-lg font-bold text-amber-600">{stats.prix_m2_moyen?.toLocaleString()}€</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center border border-slate-100">
              <div className="text-[10px] sm:text-xs text-slate-500 uppercase">Médian</div>
              <div className="text-sm sm:text-lg font-bold text-slate-700">{stats.prix_median?.toLocaleString()}€</div>
            </div>
          </div>
          {expanded && data.transactions && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-500 mb-2 uppercase">Dernières transactions</p>
              <div className="space-y-1.5">
                {data.transactions.slice(0, 5).map((tx, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-white rounded p-2 border border-slate-100">
                    <span className="text-slate-500">{tx.date}</span>
                    <span className="font-medium">{tx.type_bien}</span>
                    <span className="font-bold text-[#0D47A1]">{tx.valeur?.toLocaleString()}€</span>
                    <span className="text-slate-500">{tx.surface}m²</span>
                    <span className="text-amber-600">{tx.prix_m2?.toLocaleString()}€/m²</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (msg.skill_used === 'diagnostic_climatique') {
      const impact = data.impact_investissement || {};
      return (
        <div data-testid="skill-card-climat" className="mt-3 bg-gradient-to-br from-slate-50 to-sky-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => toggleSkillCard(idx)}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-sky-500 to-cyan-500 flex items-center justify-center">
                <CloudSun className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-sm text-slate-700">Climat - {data.commune}</span>
              <Badge variant="outline" className="text-xs">{data.zone_climatique}</Badge>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xs text-slate-500 uppercase">Temp. moyenne</div>
              <div className="text-xl font-bold text-sky-600">{data.temperature?.mean_annual}°C</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xs text-slate-500 uppercase">Risque investissement</div>
              <div className={`text-lg font-bold ${impact.niveau_risque === 'élevé' ? 'text-red-600' : impact.niveau_risque === 'modéré' ? 'text-amber-600' : 'text-green-600'}`}>
                {impact.niveau_risque?.toUpperCase()}
              </div>
            </div>
          </div>
          {expanded && (
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-3 text-sm">
              {impact.risques_principaux && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase mb-1">Risques principaux</p>
                  <div className="flex flex-wrap gap-1">
                    {impact.risques_principaux.map((r, i) => (
                      <Badge key={i} variant="outline" className="text-xs border-red-200 text-red-700 bg-red-50">{r}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {impact.recommandations && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase mb-1">Recommandations</p>
                  {impact.recommandations.map((r, i) => (
                    <p key={i} className="text-xs text-slate-600 pl-3 border-l-2 border-sky-300 mb-1">{r}</p>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500 italic">{impact.impact_valeur}</p>
            </div>
          )}
        </div>
      );
    }

    if (msg.skill_used === 'estimer_prix') {
      return (
        <div data-testid="skill-card-estimation" className="mt-3 bg-gradient-to-br from-slate-50 to-emerald-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => toggleSkillCard(idx)}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 flex items-center justify-center">
                <Tag className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-sm text-slate-700">Estimation - {data.commune}</span>
              <Badge variant="outline" className="text-xs">{data.type_bien} {data.surface}m²</Badge>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center border border-slate-100">
              <div className="text-[10px] sm:text-xs text-slate-500 uppercase">Est. basse</div>
              <div className="text-sm sm:text-lg font-bold text-slate-600">{data.estimation_basse?.toLocaleString()}€</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center border border-slate-100 ring-2 ring-emerald-200">
              <div className="text-[10px] sm:text-xs text-slate-500 uppercase">Est. moyenne</div>
              <div className="text-base sm:text-xl font-bold text-emerald-600">{data.estimation_moyenne?.toLocaleString()}€</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center border border-slate-100">
              <div className="text-[10px] sm:text-xs text-slate-500 uppercase">Est. haute</div>
              <div className="text-sm sm:text-lg font-bold text-slate-600">{data.estimation_haute?.toLocaleString()}€</div>
            </div>
          </div>
          {expanded && (
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Prix/m² médian</span><span className="font-medium">{data.prix_m2_median?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Prix/m² bas</span><span className="font-medium">{data.prix_m2_bas?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Prix/m² haut</span><span className="font-medium">{data.prix_m2_haut?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Transactions analysées</span><span className="font-medium">{data.nb_transactions_analysees}</span></div>
              <p className="text-xs text-slate-400 italic">Source: {data.source}</p>
            </div>
          )}
        </div>
      );
    }

    if (msg.skill_used === 'analyser_bien') {
      const bien = data.bien || {};
      const rent = data.rentabilite || {};
      const credit = data.credit_25ans || {};
      const isPositif = data.verdict === 'positif';
      return (
        <div data-testid="skill-card-analyse" className="mt-3 bg-gradient-to-br from-slate-50 to-violet-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => toggleSkillCard(idx)}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isPositif ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-orange-500 to-red-500'}`}>
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-sm text-slate-700">Analyse d'Investissement</span>
              <Badge className={`text-xs ${isPositif ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {isPositif ? 'Cash-flow positif' : 'Cash-flow négatif'}
              </Badge>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
          {/* Property summary */}
          <div className="bg-white rounded-lg p-3 border border-slate-100 mb-3">
            <p className="text-sm font-medium text-slate-800">{bien.titre}</p>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
              <span className="font-bold text-lg text-[#0D47A1]">{bien.prix?.toLocaleString()}€</span>
              {bien.surface > 0 && <span>{bien.surface}m²</span>}
              {bien.localisation && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{bien.localisation}</span>}
              {bien.url && <a href={bien.url} target="_blank" rel="noopener noreferrer" className="text-[#0D47A1] hover:underline flex items-center gap-0.5"><ExternalLink className="h-3 w-3" />Annonce</a>}
            </div>
          </div>
          {/* Key metrics */}
          <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
            <div className="bg-white rounded-lg p-1.5 sm:p-2.5 text-center border border-slate-100">
              <div className="text-[9px] sm:text-[10px] text-slate-500 uppercase">Rdt brut</div>
              <div className="text-sm sm:text-lg font-bold text-[#0D47A1]">{rent.rendement_brut?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded-lg p-1.5 sm:p-2.5 text-center border border-slate-100">
              <div className="text-[9px] sm:text-[10px] text-slate-500 uppercase">Rdt net</div>
              <div className="text-sm sm:text-lg font-bold text-[#00B050]">{rent.rendement_net?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded-lg p-1.5 sm:p-2.5 text-center border border-slate-100">
              <div className="text-[9px] sm:text-[10px] text-slate-500 uppercase">Cash-flow</div>
              <div className={`text-sm sm:text-lg font-bold ${rent.cash_flow_mensuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {rent.cash_flow_mensuel >= 0 ? '+' : ''}{rent.cash_flow_mensuel?.toLocaleString()}€/m
              </div>
            </div>
          </div>
          {expanded && (
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Loyer estimé</span><span className="font-medium">{data.loyer_estime?.toLocaleString()}€/mois</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Mensualité crédit (25 ans)</span><span className="font-medium">{credit.mensualite?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Coût des intérêts</span><span className="font-medium text-red-600">{credit.cout_interets?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Impôts annuels</span><span className="font-medium text-red-600">{rent.impots_annuels?.toLocaleString()}€</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Régime fiscal</span><Badge className="bg-[#0D47A1] text-white text-xs">{rent.regime_label}</Badge></div>
            </div>
          )}
          {/* Save to portfolio button */}
          <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
            <Button
              size="sm"
              className={`text-xs ${savedAnalyses[JSON.stringify(data.bien)] ? 'bg-green-600' : 'bg-gradient-to-r from-[#0D47A1] to-[#00B050]'}`}
              onClick={() => saveToPortfolio(data)}
              disabled={savedAnalyses[JSON.stringify(data.bien)]}
              data-testid="save-to-portfolio-btn"
            >
              {savedAnalyses[JSON.stringify(data.bien)] ? (
                <><CheckCircle2 className="h-3 w-3 mr-1" /> Enregistré</>
              ) : (
                <><Briefcase className="h-3 w-3 mr-1" /> Enregistrer dans Portfolio</>
              )}
            </Button>
            <Link to="/portfolio" className="text-xs text-[#0D47A1] hover:underline flex items-center gap-1">
              <Briefcase className="h-3 w-3" /> Mon portfolio
            </Link>
          </div>
        </div>
      );
    }

    if (msg.skill_used === 'rechercher_biens') {
      const biens = data.biens || [];
      return (
        <div data-testid="skill-card-biens" className="mt-3 bg-gradient-to-br from-slate-50 to-rose-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => toggleSkillCard(idx)}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-rose-500 to-pink-500 flex items-center justify-center">
                <Home className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-sm text-slate-700">Biens à {data.ville}</span>
              <Badge variant="outline" className="text-xs">{data.nb_resultats} résultats</Badge>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
          {data.nb_resultats > 0 && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
                <div className="text-xs text-slate-500 uppercase">Prix moyen</div>
                <div className="text-lg font-bold text-rose-600">{data.prix_moyen?.toLocaleString()}€</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border border-slate-100">
                <div className="text-xs text-slate-500 uppercase">Surface moyenne</div>
                <div className="text-lg font-bold text-slate-700">{data.surface_moyenne}m²</div>
              </div>
            </div>
          )}
          {/* Property listings */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {biens.slice(0, expanded ? 8 : 3).map((bien, i) => (
              <div key={i} className="bg-white rounded-lg p-3 border border-slate-100 hover:border-rose-200 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{bien.titre}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{bien.localisation}</span>
                      {bien.surface > 0 && <span>{bien.surface}m²</span>}
                      {bien.nb_pieces > 0 && <span>{bien.nb_pieces}p</span>}
                      {bien.dpe && <Badge variant="outline" className="text-xs">DPE {bien.dpe}</Badge>}
                    </div>
                    {bien.caracteristiques?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {bien.caracteristiques.slice(0, 3).map((c, j) => (
                          <span key={j} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{c}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right ml-3 flex-shrink-0">
                    <div className="text-lg font-bold text-rose-600">{bien.prix?.toLocaleString()}€</div>
                    {bien.rendement_brut > 0 && (
                      <div className="text-xs text-green-600">~{bien.rendement_brut}% brut</div>
                    )}
                    {bien.url && (
                      <a href={bien.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0D47A1] hover:underline flex items-center gap-0.5 justify-end mt-1">
                        <ExternalLink className="h-3 w-3" />Voir
                      </a>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 w-full text-xs border-[#0D47A1] text-[#0D47A1] hover:bg-[#0D47A1] hover:text-white"
                  onClick={() => analyzeProperty(bien, i)}
                  disabled={chatLoading}
                  data-testid={`analyze-bien-${i}`}
                >
                  {chatLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <TrendingUp className="h-3 w-3 mr-1" />}
                  Analyser Bien N°{i + 1}
                </Button>
              </div>
            ))}
          </div>
          {biens.length > 3 && !expanded && (
            <p className="text-xs text-center text-slate-400 mt-2 cursor-pointer hover:text-slate-600" onClick={() => toggleSkillCard(idx)}>
              + {biens.length - 3} autres biens...
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  // Render Chat Tab component
  const renderChatTab = () => (
    <div className="space-y-4">
      {/* Usage Stats for Free users */}
      {isFree && usageStats && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800 text-sm">Version gratuite</p>
                <p className="text-xs sm:text-sm text-amber-600">
                  Il te reste <strong>{usageStats.chat?.remaining || 0}</strong> question(s) aujourd'hui
                </p>
              </div>
            </div>
            {!isNativePlatform() ? (
            <Link to="/pricing">
              <Button size="sm" className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white w-full sm:w-auto">
                <Crown className="h-4 w-4 mr-2" />
                Passer au Club
              </Button>
            </Link>
            ) : (
              <p className="text-xs text-amber-600 font-medium">Limite atteinte pour aujourd'hui</p>
            )}
          </div>
        </div>
      )}

      {/* Chat Container */}
      <Card className="border-slate-200">
        <CardHeader className="border-b bg-gradient-to-r from-[#00B050]/10 to-[#0D47A1]/10 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center p-1 flex-shrink-0">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/3n7jizcm_LOGO%2010MILLIONSINVESTOR%20blanc.png" 
                  alt="10M investor" 
                  className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                />
              </div>
              <div>
                <CardTitle className="text-sm sm:text-lg">Assistant 10M Investor</CardTitle>
                <CardDescription className="text-xs sm:text-sm hidden sm:block">Pose tes questions sur l'investissement immobilier</CardDescription>
              </div>
            </div>
            {chatMessages.length > 0 && (
              <Button variant="outline" size="sm" onClick={clearChat} className="border-[#0D47A1] text-[#0D47A1] hover:bg-[#0D47A1]/5" data-testid="new-chat-btn">
                <RefreshCw className="h-4 w-4 mr-1" /> Nouvelle demande
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Messages Area */}
          <div className="h-[calc(100vh-380px)] min-h-[300px] max-h-[600px] overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
            {chatMessages.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#00B050]/10 to-[#0D47A1]/10 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/3n7jizcm_LOGO%2010MILLIONSINVESTOR%20blanc.png" 
                    alt="10M investor" 
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-2">Comment puis-je t'aider ?</h4>
                <p className="text-slate-500 text-xs sm:text-sm mb-4">
                  Pose-moi une question sur l'immobilier{isClub ? ' ou uploade un document à analyser' : ''}
                </p>
                {/* Skills badges */}
                {availableSkills.length > 0 && (
                  <div className="mb-4">
                    <button onClick={() => setShowSkills(!showSkills)} className="text-xs text-[#0D47A1] flex items-center gap-1 mx-auto mb-2 hover:underline">
                      <Zap className="h-3 w-3" />
                      {showSkills ? 'Masquer' : 'Voir'} les {availableSkills.length} compétences IA
                    </button>
                    {showSkills && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left max-w-lg mx-auto" data-testid="skills-grid">
                        {availableSkills.map((skill, i) => (
                          <button
                            key={skill.id}
                            data-testid={`skill-btn-${skill.id}`}
                            onClick={() => setChatInput(skill.example)}
                            className="flex items-start gap-2 p-2.5 bg-white border border-slate-200 rounded-lg hover:border-[#0D47A1]/30 hover:shadow-sm transition-all text-left"
                          >
                            <div className="w-7 h-7 rounded-md bg-gradient-to-r from-[#00B050] to-[#0D47A1] flex items-center justify-center flex-shrink-0">
                              {getSkillIcon(skill.id)}
                              <span className="text-white" style={{ display: 'none' }}>{skill.icon}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-slate-700">{skill.label}</p>
                              <p className="text-xs text-slate-400 truncate">{skill.description}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap justify-center gap-2">
                  {['Calcule la rentabilité d\'un T3 à 180000€, loyer 750€', 'Simule un crédit de 200000€ sur 25 ans', 'Prix au m² à Bordeaux ?'].map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setChatInput(q)}
                      className="px-3 py-1.5 text-xs sm:text-sm bg-slate-100 text-slate-600 rounded-full hover:bg-slate-200 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[95%] sm:max-w-[85%] ${msg.role === 'user' ? '' : 'w-full max-w-[95%] sm:max-w-[85%]'}`}>
                  {/* Skill badge */}
                  {msg.skill_used && msg.skill_label && (
                    <div className="flex items-center gap-1.5 mb-1.5 ml-1">
                      {getSkillIcon(msg.skill_used)}
                      <span className="text-xs font-medium text-[#0D47A1] bg-[#0D47A1]/10 px-2 py-0.5 rounded-full">
                        {msg.skill_label}
                      </span>
                    </div>
                  )}
                  <div className={`rounded-2xl px-4 py-2.5 ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white'
                      : 'bg-white text-slate-800 border border-slate-200 shadow-sm'
                  }`}>
                    {msg.has_pdf && <FileText className="w-4 h-4 inline mr-2" />}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {/* Skill result card */}
                  {renderSkillData(msg, idx)}
                </div>
              </div>
            ))}
            
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-2xl px-4 py-3">
                  <Loader2 className="w-5 h-5 text-[#0D47A1] animate-spin" />
                </div>
              </div>
            )}
            
            <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t p-2 sm:p-4 bg-slate-50">
            <div className="flex items-center gap-1.5 sm:gap-2">
              {isClub && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePdfUpload}
                    accept=".pdf"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={chatLoading}
                    title="Uploader un PDF"
                    className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0"
                  >
                    <Upload className="w-4 h-4" />
                  </Button>
                </>
              )}
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                placeholder={isFree && usageStats?.chat?.remaining <= 0 ? "Limite atteinte" : "Pose ta question..."}
                disabled={chatLoading || (isFree && usageStats?.chat?.remaining <= 0)}
                className="flex-1 text-sm sm:text-base h-9 sm:h-10"
              />
              <Button
                variant={isListening ? "destructive" : "outline"}
                size="icon"
                onClick={toggleListening}
                disabled={chatLoading}
                title={isListening ? "Arrêter" : "Dictée vocale"}
                data-testid="voice-mic-btn"
                className={`h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 ${isListening ? "animate-pulse" : ""}`}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              <Button
                onClick={sendChatMessage}
                disabled={!chatInput.trim() || chatLoading || (isFree && usageStats?.chat?.remaining <= 0)}
                className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] hover:opacity-90 h-9 sm:h-10 px-3 sm:px-4 flex-shrink-0"
                data-testid="ai-agent-send-btn"
              >
                {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            {!isClub && (
              <p className="text-xs text-slate-500 mt-2 text-center">
                <Crown className="h-3 w-3 inline mr-1" />
                L'upload de PDF et les questions illimitées sont réservés aux membres Club
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Non-Expert view for search - but allow chat for all
  const renderSearchTab = () => {
    if (!isExpert) {
      return (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#00B050]/10 to-[#0D47A1]/10 flex items-center justify-center">
              <Search className="w-8 h-8 text-[#0D47A1]" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Recherche de biens</h3>
            <p className="text-slate-600 mb-4">
              L'Agent IA scrape les annonces immobilières et les analyse automatiquement selon tes critères.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 text-left">
              <div className="p-4 bg-slate-50 rounded-lg">
                <Sparkles className="h-6 w-6 text-[#0D47A1] mb-2" />
                <h4 className="font-medium">Recherche intelligente</h4>
                <p className="text-sm text-slate-500">L'IA scrape les annonces et les analyse</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <Target className="h-6 w-6 text-green-600 mb-2" />
                <h4 className="font-medium">Score de compatibilité</h4>
                <p className="text-sm text-slate-500">Chaque annonce est notée selon ton profil</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <BarChart3 className="h-6 w-6 text-blue-600 mb-2" />
                <h4 className="font-medium">Analyse rentabilité</h4>
                <p className="text-sm text-slate-500">Estimation loyer, rendement automatique</p>
              </div>
            </div>
            {!isNativePlatform() ? (
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white">
                <Crown className="h-4 w-4 mr-2" />
                Débloquer avec l'abonnement Club
              </Button>
            </Link>
            ) : (
              <p className="text-sm text-slate-500 font-medium mt-2">Bientôt disponible</p>
            )}
          </CardContent>
        </Card>
      );
    }

    // Expert view - search interface rendered separately below
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AIConsentModal
        open={needsAIConsent}
        onAccept={() => setNeedsAIConsent(false)}
        onDecline={() => {
          setNeedsAIConsent(false);
          toast.info('Consentement requis pour utiliser l\'IA. Vous serez redirigé.');
          navigate('/dashboard');
        }}
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Tableau de bord
                </Button>
              </Link>
              <Link to="/dashboard" className="hidden md:flex">
                <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" data-testid="header-logo" />
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-4">
              <Link to="/calculator" className="text-slate-600 hover:text-[#0D47A1] transition-colors text-sm">Calculateur</Link>
              <Link to="/mdb" className="text-slate-600 hover:text-[#0D47A1] transition-colors text-sm">MDB</Link>
              <Link to="/community" className="text-slate-600 hover:text-[#0D47A1] transition-colors text-sm">Communauté</Link>
            </nav>

            <div className="hidden md:flex items-center space-x-3">
              <Link to="/ai-stats">
                <Button variant="outline" size="sm" className="border-[#0D47A1]/20 text-[#0D47A1] hover:bg-[#0D47A1]/5">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Mes Stats
                </Button>
              </Link>
              <Badge className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white">Club</Badge>
              <Button variant="ghost" size="sm" onClick={() => navigate('/profile')}>
                <User className="h-4 w-4 mr-2" />
                {user?.name}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600 hover:text-red-600">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>

            {/* Mobile menu button */}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t">
              <nav className="flex flex-col space-y-3">
                <Link to="/dashboard" className="text-slate-600 hover:text-[#0D47A1]">Tableau de bord</Link>
                <Link to="/calculator" className="text-slate-600 hover:text-[#0D47A1]">Calculateur</Link>
                <Link to="/mdb" className="text-slate-600 hover:text-[#0D47A1]">MDB</Link>
                <Link to="/community" className="text-slate-600 hover:text-[#0D47A1]">Communauté</Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-[#00B050] to-[#0D47A1] rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Bot className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0D47A1]">Agent IA Immobilier</h1>
              <p className="text-xs sm:text-sm text-slate-600">Assistant IA avec compétences d'analyse</p>
            </div>
          </div>
          {isExpert && (
            <Button 
              variant="outline" 
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2"
            >
              <History className="h-4 w-4" />
              Historique ({searchHistory.length})
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 sm:mb-6 border-b border-slate-200 pb-3 sm:pb-4">
          <Button
            variant={activeTab === 'chat' ? 'default' : 'outline'}
            onClick={() => setActiveTab('chat')}
            className={`flex-1 sm:flex-none text-xs sm:text-sm ${activeTab === 'chat' ? 'bg-gradient-to-r from-[#00B050] to-[#0D47A1] hover:opacity-90' : ''}`}
          >
            <Zap className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Chat IA + Skills</span>
            <span className="sm:hidden">Chat IA</span>
          </Button>
          <Button
            variant={activeTab === 'search' ? 'default' : 'outline'}
            onClick={() => setActiveTab('search')}
            className={`flex-1 sm:flex-none text-xs sm:text-sm ${activeTab === 'search' ? 'bg-[#0D47A1] hover:bg-[#0D47A1]/90' : ''}`}
          >
            <Search className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Recherche de biens</span>
            <span className="sm:hidden">Recherche</span>
            {!isExpert && <Badge className="ml-1 sm:ml-2 bg-amber-500 text-white text-[10px] sm:text-xs">Club</Badge>}
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'chat' ? (
          renderChatTab()
        ) : (
          renderSearchTab()
        )}

        {/* Expert Search Interface - only show when search tab is active AND user is expert */}
        {activeTab === 'search' && isExpert && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
          {/* Search Criteria Panel */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg border-0 sticky top-24">
              <CardHeader className="bg-gradient-to-r from-[#0D47A1] to-[#1565C0] text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Critères de recherche
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 pt-6">
                {/* Location */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#00B050]" />
                    Localisation
                  </Label>
                  <Input
                    data-testid="search-location"
                    placeholder="Lyon, Paris, Marseille..."
                    value={criteria.localisation}
                    onChange={(e) => setCriteria({ ...criteria, localisation: e.target.value })}
                  />
                </div>

                {/* Property Type */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-[#00B050]" />
                    Type de bien
                  </Label>
                  <Select 
                    value={criteria.type_bien}
                    onValueChange={(value) => setCriteria({ ...criteria, type_bien: value })}
                  >
                    <SelectTrigger data-testid="search-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appartement">Appartement</SelectItem>
                      <SelectItem value="maison">Maison</SelectItem>
                      <SelectItem value="immeuble">Immeuble</SelectItem>
                      <SelectItem value="local_commercial">Local commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Budget */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Euro className="h-4 w-4 text-[#00B050]" />
                    Budget maximum
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="search-budget"
                      type="number"
                      placeholder="300000"
                      value={criteria.budget_max || ''}
                      onChange={(e) => setCriteria({ ...criteria, budget_max: parseFloat(e.target.value) || null })}
                    />
                    <span className="text-slate-500">€</span>
                  </div>
                </div>

                {/* Surface */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Ruler className="h-4 w-4 text-[#00B050]" />
                    Surface minimum (m²)
                  </Label>
                  <Input
                    data-testid="search-surface"
                    type="number"
                    placeholder="50"
                    value={criteria.surface_min || ''}
                    onChange={(e) => setCriteria({ ...criteria, surface_min: parseFloat(e.target.value) || null })}
                  />
                </div>

                {/* Minimum Yield */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-[#00B050]" />
                    Rendement brut minimum
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="search-yield"
                      type="number"
                      step="0.5"
                      placeholder="5"
                      value={criteria.rendement_min || ''}
                      onChange={(e) => setCriteria({ ...criteria, rendement_min: parseFloat(e.target.value) || null })}
                    />
                    <span className="text-slate-500">%</span>
                  </div>
                </div>

                <Button 
                  data-testid="search-submit"
                  className="w-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] hover:opacity-90 mt-4"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Lancer la recherche IA
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {/* From MDB Banner */}
            {fromMDB && (
              <Card className="mb-6 shadow-lg border-0 bg-gradient-to-r from-[#00B050]/10 to-[#0D47A1]/10">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-[#00B050]" />
                      <span className="font-medium">Recherche depuis le projet MDB : <span className="text-[#0D47A1]">{mdbProjectName}</span></span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/mdb')}>
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Retour MDB
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Viewing Historic Search Banner */}
            {viewingHistoricSearch && (
              <Card className="mb-6 shadow-lg border-0 bg-amber-50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <History className="h-5 w-5 text-amber-600" />
                      <span className="font-medium text-amber-800">
                        Recherche du {new Date(viewingHistoricSearch.created_at).toLocaleDateString('fr-FR', { 
                          day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                        })}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={clearHistoricView}>
                        <X className="h-4 w-4 mr-1" />
                        Fermer
                      </Button>
                      <Button size="sm" className="bg-[#0D47A1]" onClick={handleSearch}>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Relancer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Search History */}
            {showHistory && searchHistory.length > 0 && (
              <Card className="mb-6 shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="h-5 w-5 text-[#0D47A1]" />
                    Recherches récentes
                  </CardTitle>
                  <CardDescription>Cliquez sur une recherche pour revoir les résultats</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {searchHistory.slice(0, 10).map((search, index) => (
                      <div 
                        key={search.search_id || index}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-all cursor-pointer gap-2"
                        onClick={() => loadHistoricSearch(search.search_id)}
                        data-testid={`history-item-${index}`}
                      >
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                          <MapPin className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <span className="font-medium text-sm">{search.criteria?.localisation || 'N/A'}</span>
                          <Badge variant="outline" className="text-xs">{search.criteria?.type_bien || 'N/A'}</Badge>
                          <span className="text-xs sm:text-sm text-slate-500">
                            {(search.criteria?.budget_max || 0).toLocaleString()}€
                          </span>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 ml-6 sm:ml-0">
                          <span className="text-xs text-slate-400">
                            {new Date(search.created_at).toLocaleDateString('fr-FR')}
                          </span>
                          <Badge className="bg-[#0D47A1] text-xs">{search.results_count} biens</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading History */}
            {loadingHistory && (
              <Card className="shadow-lg border-0">
                <CardContent className="py-16">
                  <div className="text-center">
                    <Loader2 className="h-12 w-12 text-[#0D47A1] mx-auto mb-4 animate-spin" />
                    <h3 className="text-xl font-semibold text-[#0D47A1] mb-2">Chargement de la recherche...</h3>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading State - Enhanced UX */}
            {loading && (
              <Card className="shadow-lg border-0">
                <CardContent className="py-10">
                  <div className="max-w-md mx-auto">
                    {/* Animated Bot Icon */}
                    <div className="text-center mb-8">
                      <div className="relative mx-auto w-24 h-24 mb-4">
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] opacity-20 animate-ping"></div>
                        <div className="absolute inset-2 rounded-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] opacity-30 animate-pulse"></div>
                        <div className="absolute inset-4 rounded-full bg-white flex items-center justify-center shadow-lg">
                          <Bot className="h-10 w-10 text-[#0D47A1] animate-bounce" />
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-[#0D47A1] mb-1">Agent IA en action</h3>
                      <p className="text-slate-500 text-sm">Analyse approfondie en cours...</p>
                    </div>

                    {/* Progress Steps */}
                    <div className="mb-8">
                      <div className="flex justify-between mb-3">
                        {loadingSteps.map((step, index) => (
                          <div key={index} className="flex flex-col items-center flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-1 transition-all duration-500 ${
                              index < loadingStep 
                                ? 'bg-[#00B050] text-white' 
                                : index === loadingStep 
                                  ? 'bg-[#0D47A1] text-white animate-pulse' 
                                  : 'bg-slate-200 text-slate-400'
                            }`}>
                              {index < loadingStep ? (
                                <CheckCircle2 className="h-4 w-4" />
                              ) : (
                                index + 1
                              )}
                            </div>
                            <span className={`text-xs text-center ${
                              index <= loadingStep ? 'text-slate-700 font-medium' : 'text-slate-400'
                            }`}>
                              {step.label}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] transition-all duration-1000 ease-out"
                          style={{ width: `${((loadingStep + 1) / loadingSteps.length) * 100}%` }}
                        ></div>
                      </div>
                      
                      {/* Current Step Description */}
                      <p className="text-center text-sm text-[#0D47A1] mt-3 font-medium animate-pulse">
                        {loadingSteps[loadingStep]?.description}
                      </p>
                    </div>

                    {/* Estimated Time */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Temps estimé : 45-90 secondes</p>
                          <p className="text-xs text-amber-600">L'IA analyse chaque annonce en détail pour vous</p>
                        </div>
                      </div>
                    </div>

                    {/* Rotating Tips */}
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-slate-600 transition-opacity duration-500">
                        {loadingMessage}
                      </p>
                    </div>

                    {/* What's happening */}
                    <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <Search className="h-5 w-5 text-[#0D47A1] mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Scan annonces</p>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <BarChart3 className="h-5 w-5 text-[#00B050] mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Calcul rentabilité</p>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <Target className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Score matching</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Results State */}
            {!loading && !results && (
              <Card className="shadow-lg border-0">
                <CardContent className="py-16">
                  <div className="text-center">
                    <div className="mx-auto w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-6">
                      <Search className="h-10 w-10 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">Prêt à chercher</h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                      Définissez vos critères de recherche et laissez l'IA trouver et analyser les meilleures opportunités pour vous.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results */}
            {!loading && results && (
              <div className="space-y-4">
                {/* Results Summary */}
                <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#00B050]" />
                    <span className="font-medium">{results.total_results} annonces analysées</span>
                    {results.listings && results.listings.filter(l => l.is_real).length > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                        {results.listings.filter(l => l.is_real).length} annonces réelles
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#0D47A1]">
                      {results.criteria?.localisation}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setResults(null); setViewingHistoricSearch(null); }}
                      className="border-[#0D47A1] text-[#0D47A1] hover:bg-[#0D47A1]/5"
                      data-testid="new-search-btn"
                    >
                      <Search className="h-4 w-4 mr-1" />
                      Nouvelle recherche
                    </Button>
                  </div>
                </div>

                {/* Listings */}
                {results.listings && results.listings.map((listing) => (
                  <Card 
                    key={listing.listing_id} 
                    className="shadow-lg border-0 overflow-hidden hover:shadow-xl transition-shadow"
                    data-testid="listing-card"
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      {listing.image_url && (
                        <div className="md:w-1/3 h-48 md:h-auto">
                          <img 
                            src={listing.image_url} 
                            alt={listing.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4 mb-3">
                          <div>
                            <h3 className="text-base sm:text-lg font-semibold text-slate-900">{listing.title}</h3>
                            <div className="flex items-center gap-2 text-slate-600 mt-1 flex-wrap text-xs sm:text-sm">
                              <MapPin className="h-4 w-4" />
                              <span>{listing.localisation}</span>
                              {listing.location_match === 'CORRESPONDANCE EXACTE' && (
                                <Badge data-testid="location-exact-badge" className="bg-green-100 text-green-700 border-green-200">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Localisation exacte
                                </Badge>
                              )}
                              {listing.location_match === 'PROXIMITÉ (ville limitrophe)' && (
                                <Badge data-testid="location-nearby-badge" className="bg-amber-100 text-amber-700 border-amber-200">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  Ville limitrophe
                                </Badge>
                              )}
                              {listing.location_match === 'HORS ZONE RECHERCHÉE' && (
                                <Badge data-testid="location-out-badge" className="bg-red-100 text-red-700 border-red-200">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Hors zone
                                </Badge>
                              )}
                              <Badge variant="outline" className="ml-1">{listing.source}</Badge>
                              {listing.is_real && (
                                <Badge data-testid="real-listing-badge" className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                                  Annonce réelle
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl sm:text-2xl font-bold text-[#0D47A1]">
                              {listing.price?.toLocaleString()}€
                            </div>
                            {listing.surface && (
                              <div className="text-sm text-slate-500">
                                {Math.round(listing.price / listing.surface)}€/m²
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Property Details */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {listing.surface && (
                            <div className="flex items-center gap-1 text-sm bg-slate-100 px-2 py-1 rounded">
                              <Ruler className="h-3 w-3" />
                              {listing.surface} m²
                            </div>
                          )}
                          {listing.nb_pieces && (
                            <div className="flex items-center gap-1 text-sm bg-slate-100 px-2 py-1 rounded">
                              <Home className="h-3 w-3" />
                              {listing.nb_pieces} pièces
                            </div>
                          )}
                          {listing.etat === 'renovation' && (
                            <div className="flex items-center gap-1 text-sm bg-orange-100 text-orange-700 px-2 py-1 rounded">
                              À rénover
                            </div>
                          )}
                          {listing.etat === 'premium' && (
                            <div className="flex items-center gap-1 text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded">
                              Premium
                            </div>
                          )}
                        </div>

                        {/* Caractéristiques */}
                        {listing.caracteristiques && listing.caracteristiques.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {listing.caracteristiques.slice(0, 5).map((carac, idx) => (
                              <span key={idx} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                                {carac}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* AI Analysis */}
                        <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="h-4 w-4 text-[#00B050]" />
                            <span className="font-medium text-slate-700">Analyse IA</span>
                            <div className={`ml-auto text-lg font-bold ${getScoreColor(listing.match_score)}`}>
                              {listing.match_score}/100
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-3">
                            <div className="text-center">
                              <div className="text-[10px] sm:text-xs text-slate-500 uppercase">Loyer</div>
                              <div className="font-semibold text-sm sm:text-base text-[#0D47A1]">
                                {listing.estimated_rent?.toLocaleString()}€
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-[10px] sm:text-xs text-slate-500 uppercase">Rendement</div>
                              <div className="font-semibold text-sm sm:text-base text-[#00B050]">
                                {listing.estimated_yield?.toFixed(1)}%
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-[10px] sm:text-xs text-slate-500 uppercase">Cash-flow</div>
                              <div className={`font-semibold text-sm sm:text-base ${listing.estimated_cashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {listing.estimated_cashflow >= 0 ? '+' : ''}{listing.estimated_cashflow?.toLocaleString()}€
                              </div>
                            </div>
                          </div>

                          {listing.ai_analysis && (
                            <p className="text-sm text-slate-600 italic">"{listing.ai_analysis}"</p>
                          )}
                        </div>

                        {/* Alerts */}
                        {listing.alerts && listing.alerts.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {listing.alerts.map((alert, idx) => (
                              <div key={idx} className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded">
                                <AlertTriangle className="h-3 w-3" />
                                {alert}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                          <Badge className={getRecommendationColor(listing.recommendation)}>
                            {listing.recommendation}
                          </Badge>
                          <div className="flex gap-2 w-full sm:w-auto">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSaveListing(listing)}
                              disabled={savedListings.includes(listing.listing_id)}
                              data-testid="save-listing"
                            >
                              <Heart className={`h-4 w-4 mr-1 ${savedListings.includes(listing.listing_id) ? 'fill-red-500 text-red-500' : ''}`} />
                              {savedListings.includes(listing.listing_id) ? 'Sauvegardé' : 'Sauvegarder'}
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              className="bg-[#0D47A1] hover:bg-[#0D47A1]/90"
                              onClick={() => window.open(listing.url, '_blank')}
                              data-testid="view-listing"
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Voir l'annonce
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}

                {/* No listings found */}
                {results.listings && results.listings.length === 0 && (
                  <Card className="shadow-lg border-0">
                    <CardContent className="py-12">
                      <div className="text-center">
                        <XCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune annonce trouvée</h3>
                        <p className="text-slate-500">Essayez d'élargir vos critères de recherche</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AIAgentPage;
